import { useMemo, useState } from 'react';

import { useCeleryConfig } from '@/hooks/useCeleryConsole';
import { CeleryConfig } from '@/types/celeryConsole';
import CopyContent from '@/ui/components/CopyContent';

import { Loading, PanelCard, PanelError } from '../../../shared';
import { PollInterval } from '../../../types';

const formatValue = (value: unknown): string => {
  if (value === null) return 'null';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const ExtrasRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-wrap items-baseline gap-2 py-1 text-sm">
    <span className="text-gray-500 font-mono">{label}</span>
    <span className="font-mono break-all">{value}</span>
  </div>
);

const BespokeConfigs = ({ config }: { config: CeleryConfig }) => {
  const recorder = config.task_status_recorder;
  return (
    <PanelCard title="Bespoke Configs">
      <ExtrasRow label="queue_service_pool_max" value={String(config.queue_service_pool_max)} />
      <ExtrasRow label="queues.known" value={config.queues.known.join(', ')} />
      <ExtrasRow
        label="task_status_recorder"
        value={`enabled=${recorder.enabled}, recent_max=${recorder.recent_max}, ttl_seconds=${recorder.ttl_seconds}`}
      />
    </PanelCard>
  );
};

const CeleryConfigTable = ({ config }: { config: Record<string, unknown> }) => {
  const [filter, setFilter] = useState('');
  const entries = useMemo(() => {
    const all = Object.entries(config).sort(([a], [b]) => a.localeCompare(b));
    if (!filter.trim()) return all;
    const needle = filter.trim().toLowerCase();
    return all.filter(
      ([key, value]) =>
        key.toLowerCase().includes(needle) || formatValue(value).toLowerCase().includes(needle)
    );
  }, [config, filter]);

  return (
    <PanelCard
      title={`Celery config — ${Object.keys(config).length} keys (resolved by current_app.conf, defaults included)`}>
      <div className="mb-3 flex items-center gap-2">
        <input
          type="text"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder="Filter by key or value…"
          className="w-full max-w-sm rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <span className="text-xs text-gray-500">{entries.length} shown</span>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-2 w-1/3">Key</th>
              <th className="py-2">Value</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([key, value]) => {
              const formatted = formatValue(value);
              return (
                <tr key={key} className="border-t align-top">
                  <td className="py-2 pr-3 font-mono text-gray-700 break-all">{key}</td>
                  <td className="py-2 font-mono break-all">
                    <CopyContent content={formatted} iconPosition="left">
                      <span>{formatted}</span>
                    </CopyContent>
                  </td>
                </tr>
              );
            })}
            {entries.length === 0 && (
              <tr>
                <td colSpan={2} className="py-3 text-sm text-gray-500">
                  No keys match.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </PanelCard>
  );
};

const ConfigPanel = ({ interval }: { interval: PollInterval }) => {
  const { data, isLoading, error } = useCeleryConfig(interval);
  if (isLoading && !data) return <Loading />;
  if (error) return <PanelError title="Celery config" error={error} />;
  if (!data) return <p className="text-sm text-gray-500">No config available.</p>;
  return (
    <>
      <BespokeConfigs config={data} />
      <CeleryConfigTable config={data.celery} />
    </>
  );
};

export default ConfigPanel;
